import { FlexContainer } from '@/ui/styled-ui';
import {
  colorBlackMain,
  colorGrayDark,
  colorGrayOpacity,
  colorPurpleText,
  colorRedLight,
  colorRedMain,
  colorTealLight,
} from '@/utils/helpers/css-variables.helper';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledTitle = styled.h3`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);

  &.report-title {
    font-size: var(--small-font-size);
    font-weight: 500;
    line-height: var(--small-line-height);
  }

  .label-icon {
    fill: var(--purple-dark);
    vertical-align: middle;
    font-size: var(--small-font-size);
  }
`;

const StyledCart = styled.div<{ isTealColor?: boolean }>`
  position: relative;
  .count-label {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 700;
    color: ${({ isTealColor }) =>
      isTealColor ? 'var(--teal-main)' : 'var(--red-main)'};
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .count {
      font-size: calc(2 * var(--small-font-size));
      line-height: var(--small-line-height);
      font-weight: 700;
      color: var(--black-main);

      &.min-score {
        font-size: var(--heading-medium-line-height);
      }
    }

    .all-count {
      color: var(--gray-darker);
    }
  }
`;

const Dot = styled.div<{ isTealColor?: boolean }>`
  width: 16px;
  height: 16px;
  background: ${({ isTealColor }) =>
    isTealColor ? 'var(--teal-main)' : 'var(--red-main)'};
  border-radius: 50%;
  margin-right: var(--xxsmall-padding);

  &.benchmark {
    margin-left: var(--medium-padding);
    background: ${({ isTealColor }) =>
      isTealColor ? 'var(--teal-light)' : 'var(--red-light)'};
  }
`;

export type DoughnutChartProps = {
  id: string;
  label?: string;
  allCount: number;
  singleCount: number;
  isBenchmark?: boolean;
  benchmark?: {
    allCount: number;
    singleCount: number;
  };
  minScore?: number;
  cutout?: string;
  mainColor?: string;
  width?: string;
  isPercentCount?: boolean;
  isDashboard?: boolean;
  labelIcon?: ReactNode;
  isLoading?: boolean;
  isTealColor?: boolean;
  isPurple?: boolean;
  disableInteraction?: boolean;
  onClick?: () => void;
};

export const DoughnutChart: React.FC<DoughnutChartProps> = ({
  id,
  label,
  allCount,
  singleCount,
  isBenchmark,
  benchmark,
  minScore,
  cutout,
  mainColor,
  width = '220px',
  isPercentCount,
  isDashboard,
  labelIcon,
  isLoading,
  isTealColor,
  isPurple,
  disableInteraction = false,
  onClick,
}) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const docStyle = getComputedStyle(document.documentElement);

  const calcPercentages = (singleCount: number, allCount: number) => {
    if (allCount !== 0) {
      const percentage = Math.round((singleCount / allCount) * 100);
      return percentage > 100 ? 100 : percentage;
    } else return 0;
  };

  useEffect(() => {
    const calculateRemainder = (singleCount: number, allCount: number) => {
      if (allCount < 1) {
        return 1;
      } else if (allCount === singleCount || allCount - singleCount < 1) {
        return 0;
      } else {
        return allCount - singleCount;
      }
    };

    const data = {
      datasets: [
        {
          label: label,
          data: !minScore
            ? [singleCount, calculateRemainder(singleCount, allCount)]
            : [
                singleCount,
                calculateRemainder(singleCount, minScore),
                calculateRemainder(
                  minScore > singleCount ? minScore : singleCount,
                  allCount,
                ),
              ],
          backgroundColor: !minScore
            ? [
                mainColor
                  ? mainColor
                  : isPurple
                  ? colorPurpleText(docStyle)
                  : colorRedMain(docStyle),
                colorGrayDark(docStyle),
              ]
            : [
                mainColor
                  ? mainColor
                  : isPurple
                  ? colorPurpleText(docStyle)
                  : colorRedMain(docStyle),
                colorBlackMain(docStyle),
                colorGrayDark(docStyle),
              ],
          borderColor: 'transparent',
        },
      ],
    };

    if (isBenchmark && benchmark) {
      data.datasets.push({
        label: label,
        data: [
          benchmark.singleCount,
          calculateRemainder(benchmark.singleCount, benchmark.allCount),
        ],
        backgroundColor: isTealColor
          ? [colorTealLight(docStyle), colorGrayOpacity(docStyle)]
          : isPurple
          ? [`${colorPurpleText(docStyle)}66`, colorGrayOpacity(docStyle)]
          : [colorRedLight(docStyle), colorGrayOpacity(docStyle)],
        borderColor: 'transparent',
      });
    }

    const options = {
      cutout: cutout ?? '80%',
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
        tooltip: { enabled: false },
      },
      ...(disableInteraction && {
        interaction: {
          mode: null,
        },
        events: [],
      }),
      responsive: false,
      maintainAspectRatio: false,
    };

    setChartData(data);
    setChartOptions(options);
  }, [allCount, singleCount, label, isPurple]);

  return (
    <FlexContainer
      direction="column"
      width="min-content"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {!isDashboard && !minScore && label && (
        <StyledTitle className="m-0 mb-4 text-center report-title">
          <svg
            className="label-icon"
            width="33"
            height="32"
            viewBox="0 0 33 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {labelIcon}
          </svg>
          {label}
        </StyledTitle>
      )}

      {isLoading ? (
        <Skeleton className="chart" shape="circle" size="160px" />
      ) : (
        <>
          <StyledCart isTealColor={isTealColor}>
            <Chart
              id={id}
              type="doughnut"
              data={chartData}
              options={chartOptions}
              className="chart"
              width={width}
            />
            <span className="count-label">
              {!isBenchmark && (
                <span className={!minScore ? 'count' : 'count min-score'}>
                  {calcPercentages(Number(singleCount), Number(allCount))}%
                </span>
              )}
              {!isPercentCount && (
                <div>
                  {singleCount} <span className="all-count"> / {allCount}</span>
                </div>
              )}
            </span>
          </StyledCart>
          {isBenchmark && (
            <FlexContainer className="count">
              <Dot isTealColor={isTealColor} />
              <h2>{calcPercentages(Number(singleCount), Number(allCount))}%</h2>
              {benchmark && (
                <>
                  <Dot isTealColor={isTealColor} className="benchmark" />
                  <h2>
                    {calcPercentages(
                      Number(benchmark.singleCount),
                      Number(benchmark.allCount),
                    )}
                    %
                  </h2>
                </>
              )}
            </FlexContainer>
          )}
        </>
      )}

      {isDashboard && !isPercentCount && (
        <StyledTitle className="m-0 mt-4 text-center report-title">
          <svg
            className="label-icon"
            width="33"
            height="32"
            viewBox="0 0 33 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {labelIcon}
          </svg>
          {label}
        </StyledTitle>
      )}
    </FlexContainer>
  );
};
